import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router-dom';
import PieChart from 'react-minimal-pie-chart';

import AgGrid from '../../shared/agGrid/agGrid.component';
import {
  alignRightCellStyle,
} from '@common/cellStyles';
import { totalProvisionQuality } from '../store/selectors';
import { BusinessesQualityPropTypes } from '../provisionQualityOverviewPage/propTypes';
import Numeral, { NumeralTypes } from '../../shared/numeral.component';
import Spinner from '../../shared/spinner.component';

import { getChartColor } from '../../shared/chartHelpers';
import Routes from '../../routes';
import {
  fetchTotalProvisionQuality,
  fetchBusinessesQuality,
} from '../store/actions';
import { generateShowLessRow, generateShowMoreRow } from '../columnDefinitions';

import columnDefs from './columnDefinitions';
import { NUMBER_OF_ITEMS_TO_DISPLAY_ON_WIDGET } from '../constants';

const getFormattedValue = value => <Numeral type={NumeralTypes.NUMBER}>{value}</Numeral>;

const containerStyle = {
  width: '100%',
};

const defaultColumnDefs = {
  ...alignRightCellStyle,
};

class ProvisionQualityWidget extends PureComponent {
  static propTypes = {
    fetchTotalProvisionQuality: PropTypes.func.isRequired,
    fetchBusinessesQuality: PropTypes.func.isRequired,

    totalProvisionQuality: PropTypes.shape({
      totalSuccess: PropTypes.number.isRequired,
      totalReady: PropTypes.number.isRequired,
      successPercent: PropTypes.number.isRequired,
    }).isRequired,
    isFetchingProvisionsTotalQuality: PropTypes.bool.isRequired,
    businessesQuality: BusinessesQualityPropTypes.isRequired,
    history: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
  };

  state = {
    showAll: false,
  };

  componentDidMount() {
    this.props.fetchTotalProvisionQuality();
    this.props.fetchBusinessesQuality();
  }

  toggleMore = () => {
    this.setState(({ showAll }) => ({ showAll: !showAll }));
  };

  showMoreRow = generateShowMoreRow({
    valueField: 'businessCode',
    onClick: this.toggleMore,
  });

  showLessRow = generateShowLessRow({
    valueField: 'businessCode',
    onClick: this.toggleMore,
  });

  navigateToProvisionQualityDashboard = () => {
    this.props.history.push(Routes.dashboard.PROVISION_QUALITY_OVERVIEW);
  };

  getCollectionToShow = () => {
    const { businessesQuality } = this.props;
    const { showAll } = this.state;

    if (businessesQuality.length <= NUMBER_OF_ITEMS_TO_DISPLAY_ON_WIDGET || showAll) {
      return businessesQuality;
    }

    return businessesQuality.slice(0, NUMBER_OF_ITEMS_TO_DISPLAY_ON_WIDGET);
  };

  getPinnedBottomRowData = () => {
    const { businessesQuality } = this.props;
    const { showAll } = this.state;

    if (businessesQuality.length <= NUMBER_OF_ITEMS_TO_DISPLAY_ON_WIDGET) {
      return null;
    }

    return showAll ? [this.showLessRow] : [this.showMoreRow];
  };

  postProcessPopup = params => {
    if (params.type !== 'columnMenu') {
      return;
    }
    const ePopup = params.ePopup;
    ePopup.style.position = 'absolute';
    ePopup.style.minHeight = '281px';
  };

  render() {
    const {
      totalProvisionQuality,
      isFetchingProvisionsTotalQuality,
    } = this.props;

    const totalPercentage = Math.floor(totalProvisionQuality.successPercent * 100);
    const collectionToShow = this.getCollectionToShow();

    return (
      <div className="widget provision-quality-widget a-panel mb-3">
        <div className="card-header"><h5>Provision Quality</h5></div>
        <div className="card-body">
          {
            isFetchingProvisionsTotalQuality
              ? <Spinner />
              : (
                <div className="row">
                  <div className="widget-pie-chart-container d-none d-lg-block col-lg-3">
                    <div className="widget-pie-chart" onClick={this.navigateToProvisionQualityDashboard}>
                      <div className="widget-total-percentage">
                        {totalPercentage}%
                      </div>
                      <PieChart
                        data={[
                          { value: totalPercentage, key: 1, color: getChartColor(totalPercentage) },
                          { value: 100 - totalPercentage, key: 2, color: '#dee2e6' },
                        ]}
                        startAngle={-90}
                        lineWidth={15}
                        totalValue={100}
                      />
                    </div>
                    <div className="widget-status-counts">
                      {getFormattedValue(totalProvisionQuality.totalSuccess)}{' '}
                      of{' '}
                      {getFormattedValue(totalProvisionQuality.totalReady)}
                      <br />
                      <div className="widget-status-label">
                        Provisions<br />
                        Complete
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-9">
                    <div style={containerStyle}>
                      <AgGrid
                        columnDefs={columnDefs}
                        defaultColDef={defaultColumnDefs}
                        domLayout="autoHeight"
                        headerHeight={50}
                        rowData={collectionToShow}
                        postProcessPopup={this.postProcessPopup}
                        pinnedBottomRowData={this.getPinnedBottomRowData()}
                        suppressRowClickSelection
                      />
                    </div>
                  </div>
                </div>
              )
          }
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  const { dashboard: { provisionQuality } } = state;

  return {
    totalProvisionQuality: totalProvisionQuality(state),
    isFetchingProvisionsTotalQuality: provisionQuality.isFetchingProvisionsTotalQuality,
    businessesQuality: provisionQuality.businessesQuality,

  };
}

export default connect(
  mapStateToProps,
  {
    fetchTotalProvisionQuality,
    fetchBusinessesQuality,
  }
)(withRouter(ProvisionQualityWidget));
